import React, { useEffect, useMemo, useState } from 'react'
import {
  Autocomplete,
  Box,
  Button,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useSnackbar } from 'notistack'
import IntelLayout from './components/IntelLayout'
import { intelColors } from './components/intelTheme'
import { intelApi } from './api'
import {
  useGetAllCourierPartnersCachedQuery,
  useGetAllActiveMerchantsIdAndNameCachedQuery,
} from '../../redux/commonApiCalls'

const COURIER_FILTER = {
  andfilter: [{ field: 'is_deleted', operator: 'eq', value: '0' }],
  offset: 0,
  limit: 5000,
  skipCount: true,
  includes: 'id,name,prozoId,cpAccountCode',
}

const SCOPES = [
  { value: 'BOTH', label: 'Both Prepaid + COD' },
  { value: 'COD', label: 'COD Only' },
]

// Tokenise textarea/CSV into a deduped list of positive integer pincodes.
function parsePincodes(text) {
  if (!text) return []
  const nums = String(text)
    .split(/[\s,]+/)
    .map((s) => s.trim())
    .filter(Boolean)
    .map(Number)
    .filter((n) => Number.isInteger(n) && n > 0)
  return Array.from(new Set(nums))
}

export default function BlockNew() {
  const { enqueueSnackbar } = useSnackbar()
  const { data: couriers = [] } = useGetAllCourierPartnersCachedQuery(COURIER_FILTER)
  const { data: merchants = [] } = useGetAllActiveMerchantsIdAndNameCachedQuery()

  const [dimension, setDimension] = useState('COURIER')
  const [pincodesText, setPincodesText] = useState('')
  const [selectedCouriers, setSelectedCouriers] = useState([]) // option objects {value,label}
  const [allCouriers, setAllCouriers] = useState(false)
  const [merchantValue, setMerchantValue] = useState(null) // option object {value,label}
  const [scope, setScope] = useState('BOTH')
  const [reason, setReason] = useState('')
  const [reasonOptions, setReasonOptions] = useState([])
  const [notes, setNotes] = useState('')
  const [submitting, setSubmitting] = useState(false)

  // Reasons come from reason_master (category PINCODE_BLOCK).
  useEffect(() => {
    let cancel = false
    intelApi
      .blockReasons()
      .then((res) => {
        if (cancel) return
        const list = res?.data?.responseObj || res?.data?.data || res?.data || []
        setReasonOptions((Array.isArray(list) ? list : []).map((r) => ({ value: r.code, label: r.label || r.code })))
      })
      .catch(() => {})
    return () => {
      cancel = true
    }
  }, [])

  // Options labelled by courier ACCOUNT CODE (value = prozoId, kept internal).
  const courierOptions = useMemo(
    () =>
      (couriers || [])
        .filter((c) => c?.prozoId)
        .map((c) => ({ value: c.prozoId, label: c.cpAccountCode || c.name || c.prozoId }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [couriers]
  )
  const merchantOptions = useMemo(
    () => (merchants || []).map((m) => ({ value: m.id, label: m.name || m.id })).sort((a, b) => a.label.localeCompare(b.label)),
    [merchants]
  )

  const onCsv = (e) => {
    const f = e.target.files && e.target.files[0]
    if (!f) return
    const reader = new FileReader()
    reader.onload = () => {
      const txt = String(reader.result || '').replace(/pincode/gi, '')
      const merged = parsePincodes((pincodesText ? pincodesText + '\n' : '') + txt)
      setPincodesText(merged.join('\n'))
    }
    reader.readAsText(f)
    e.target.value = ''
  }

  const submit = async () => {
    const pincodes = parsePincodes(pincodesText)
    if (!pincodes.length) {
      enqueueSnackbar('Enter at least one pincode', { variant: 'warning' })
      return
    }
    const body = { pincodes, scope, reason, notes: notes || undefined }
    if (dimension === 'COURIER') {
      const prozoIds = allCouriers ? courierOptions.map((o) => o.value) : selectedCouriers.map((o) => o.value)
      if (!prozoIds.length) {
        enqueueSnackbar('Select courier account(s) or All Couriers', { variant: 'warning' })
        return
      }
      body.prozoIds = prozoIds
    } else {
      if (!merchantValue) {
        enqueueSnackbar('Select a merchant', { variant: 'warning' })
        return
      }
      body.merchantOid = merchantValue.value
    }
    try {
      setSubmitting(true)
      const res = await intelApi.blockCreate(body)
      const r = res?.data?.responseObj || res?.data?.data || res?.data || {}
      const updated = r.couriersUpdated
      const skipped = r.skippedCouriers
      if (dimension === 'COURIER' && !updated) {
        enqueueSnackbar(`No couriers updated — ${skipped || 0} skipped (no courier config found)`, { variant: 'warning' })
      } else {
        enqueueSnackbar(
          `Blocked ${r.pincodeCount != null ? r.pincodeCount : pincodes.length} pincode(s)` +
            (updated ? ` on ${updated} courier(s)` : '') +
            (skipped ? ` · ${skipped} skipped` : ''),
          { variant: 'success' }
        )
      }
      setPincodesText('')
      setSelectedCouriers([])
      setAllCouriers(false)
      setMerchantValue(null)
      setNotes('')
    } catch (e) {
      enqueueSnackbar(e?.response?.data?.exception || e?.response?.data?.message || e.message, {
        variant: 'error',
      })
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <IntelLayout title='Block Pincodes — Block New'>
      <Box
        sx={{
          maxWidth: 640,
          background: '#fff',
          border: `1px solid ${intelColors.border}`,
          borderRadius: '8px',
          p: '20px',
        }}
      >
        <Stack spacing={2}>
          <Box>
            <Typography sx={{ fontSize: 12, fontWeight: 700, mb: 0.5 }}>Pincodes</Typography>
            <TextField
              value={pincodesText}
              onChange={(e) => setPincodesText(e.target.value)}
              placeholder={'One per line or comma-separated\ne.g. 380051\n641032, 110020'}
              multiline
              minRows={3}
              fullWidth
              size='small'
            />
            <Button component='label' size='small' sx={{ mt: 1 }}>
              Upload CSV
              <input type='file' accept='.csv,text/csv' hidden onChange={onCsv} />
            </Button>
          </Box>

          <Box>
            <Typography sx={{ fontSize: 12, fontWeight: 700, mb: 0.5 }}>Block By</Typography>
            <RadioGroup row value={dimension} onChange={(e) => setDimension(e.target.value)}>
              <FormControlLabel value='COURIER' control={<Radio size='small' />} label='Courier' />
              <FormControlLabel value='MERCHANT' control={<Radio size='small' />} label='Merchant' />
            </RadioGroup>
          </Box>

          {dimension === 'COURIER' ? (
            <Box>
              <FormControlLabel
                control={<Radio size='small' checked={allCouriers} onClick={() => setAllCouriers((v) => !v)} />}
                label='All Couriers (Platform-wide)'
              />
              {!allCouriers && (
                <Autocomplete
                  multiple
                  options={courierOptions}
                  getOptionLabel={(o) => o.label || ''}
                  isOptionEqualToValue={(a, b) => a.value === b.value}
                  value={selectedCouriers}
                  onChange={(_, v) => setSelectedCouriers(v)}
                  size='small'
                  filterSelectedOptions
                  renderInput={(params) => (
                    <TextField {...params} label='Courier Account(s)' placeholder='Search account code…' />
                  )}
                />
              )}
            </Box>
          ) : (
            <Autocomplete
              options={merchantOptions}
              getOptionLabel={(o) => o.label || ''}
              isOptionEqualToValue={(a, b) => a.value === b.value}
              value={merchantValue}
              onChange={(_, v) => setMerchantValue(v)}
              size='small'
              renderInput={(params) => <TextField {...params} label='Merchant' placeholder='Search merchant…' />}
            />
          )}

          <TextField select label='Block Scope' value={scope} onChange={(e) => setScope(e.target.value)} fullWidth size='small'>
            {SCOPES.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label='Block Reason'
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            fullWidth
            size='small'
            helperText={reasonOptions.length ? '' : 'No reasons configured — seed reason_master (category PINCODE_BLOCK)'}
          >
            <MenuItem value=''>—</MenuItem>
            {reasonOptions.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            label='Notes (optional)'
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            multiline
            minRows={2}
            fullWidth
            size='small'
          />

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant='contained' color='error' disabled={submitting} onClick={submit}>
              {submitting ? 'Blocking…' : '🚫 Block Pincode(s)'}
            </Button>
          </Box>

          <Typography sx={{ fontSize: 11, color: intelColors.muted }}>
            One dimension per block (Courier or Merchant). Scope = payment mode (Both = prepaid + COD, or COD-only).
            Direction follows the chosen courier account (reverse couriers are separate accounts); merchant blocks apply
            across all couriers. Blocks apply at order creation, allocation, and serviceability lookup.
          </Typography>
        </Stack>
      </Box>
    </IntelLayout>
  )
}
